import type { ChangeEvent, FocusEvent } from 'react';
import { useRef } from 'react';
import { combineRefs } from '../../../common/functions/combine-refs';
import RequiredIndicator from '../../common/required-indicator/required-indicator.component';
import { mergeStyling } from '../../common-functions';
import type { DynamicFieldComponentProps } from '../../dynamic-form/dynamic-form.interfaces';
import EmptyFieldLabel from '../../labels/empty-field-label';
import ErrorLabel from '../../labels/error-label';
import FieldLabel from '../../labels/field-label';
import OdinIcon, { OdinIconSize } from '../../odin-icon/odin-icon.component';
import { getCurrentTime } from './time-utils';

export interface TimeProps extends DynamicFieldComponentProps<string | null> {
  /**
   * When `true`, automatically populates the field with the current local time on focus if the field is empty.
   *
   * @default true
   */
  populateOnFocus?: boolean;
}

export const Time = ({
  id,
  name,
  label,
  value,
  error,
  placeholder,
  className,
  inputRef,
  readOnly,
  required,
  disabled,
  onChange,
  onFocus,
  labelStyles,
  populateOnFocus = true,
}: TimeProps) => {
  const internalRef = useRef<HTMLInputElement>(null);
  const combinedRef = combineRefs(inputRef, internalRef);

  const identifier = id ? String(id) : name;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value) {
      onChange?.(value);
    } else {
      handleClear();
    }
  };

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    if (populateOnFocus && !readOnly && !event.target.value) {
      onChange?.(getCurrentTime());
    }
    onFocus?.();
  };

  const handleClear = () => {
    onChange?.(null);
  };

  const showClear = !!value && !readOnly && !disabled;
  const isEmpty = !value || value.length === 0;

  const inputStyles = mergeStyling(
    'w-full appearance-none rounded-sm border py-2 text-base placeholder-gray-3 focus:outline-hidden md:py-1 md:text-sm',
    '[&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none',
    {
      'border-gray-4 bg-mono-1 pr-8 pl-8': !readOnly && !error,
      'border-error bg-mono-1 pr-8 pl-8': !readOnly && !!error,
      'cursor-default border-none bg-transparent pl-0': readOnly,
      hidden: readOnly && isEmpty,
      'text-readonly': disabled,
    },
    className
  );

  return (
    <div className="flex w-full flex-col">
      {label && (
        <div className="flex">
          <FieldLabel label={label} name={identifier} labelStyles={labelStyles} />
          <RequiredIndicator readOnly={readOnly} required={required} />
        </div>
      )}
      <div className="relative">
        {!readOnly && (
          <div className="pointer-events-none absolute inset-y-0 left-2 flex items-center">
            <OdinIcon icon="Clock" size={OdinIconSize.ExtraSmall} />
          </div>
        )}
        <input
          ref={combinedRef}
          type="time"
          id={identifier}
          name={name}
          value={value ?? ''}
          placeholder={placeholder}
          onChange={handleChange}
          onFocus={handleFocus}
          readOnly={readOnly}
          disabled={disabled}
          aria-invalid={!!error}
          className={inputStyles}
        />
        {showClear && (
          <button
            type="button"
            tabIndex={0}
            aria-label="Clear time"
            onClick={handleClear}
            className="absolute inset-y-0 right-0 flex items-center px-2 text-gray-3 focus-visible:outline-2 focus-visible:-outline-offset-2"
          >
            <OdinIcon icon="X" size={OdinIconSize.ExtraSmall} />
          </button>
        )}
      </div>
      {readOnly && isEmpty && <EmptyFieldLabel />}
      {error && <ErrorLabel role="alert">{error}</ErrorLabel>}
    </div>
  );
};
